import { toPng } from 'html-to-image';
import { FunctionComponent, RefObject, useCallback, useState } from 'react';
import { Loader } from '../Loader';
import { DownloadIcon } from '../../icons';
import { useModal } from '../../modals/Modal';
import { useToast } from '../../layout/Toast';
import { useTranslation } from '@/hooks/useTranslation';
import clsx from 'clsx';

type ImageCaptureButtonProps = {
  targetRef: RefObject<HTMLDivElement | null>;
  downloadName: string;
  className?: string;
  iconOnly?: boolean;
};

export const ImageCaptureButton: FunctionComponent<ImageCaptureButtonProps> = ({
  targetRef,
  downloadName,
  className,
  iconOnly = false,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const modal = useModal();
  const toast = useToast();

  const onImageCapture = useCallback(() => {
    if (targetRef.current === null) {
      return;
    }

    setIsLoading(true);

    toPng(targetRef.current, {
      cacheBust: true,
      backgroundColor: 'white',
      style: { opacity: '1' },
    })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = downloadName;
        link.href = dataUrl;
        link.click();
        modal.closeModal();
      })
      .catch((err) => {
        toast.openToastWithError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [targetRef, downloadName, modal, toast]);

  return (
    <button
      disabled={isLoading}
      className={clsx(className ? className : 'primary purple')}
      onClick={onImageCapture}
    >
      {isLoading ? <Loader /> : <DownloadIcon className="w-4 h-4" />}
      {!iconOnly && (
        <span>{t('components.generic.exportable_to_image.action')}</span>
      )}
    </button>
  );
};
